import { useState } from 'react'
import { ChevronDown, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MetadataSectionProps {
	title: string
	icon: LucideIcon
	children: React.ReactNode
	className?: string
	collapsible?: boolean
	defaultOpen?: boolean
	iconWrapperClassName?: string
	contentClassName?: string
}

export function MetadataSection({
	title,
	icon: Icon,
	children,
	className,
	collapsible = false,
	defaultOpen = true,
	iconWrapperClassName,
	contentClassName,
}: MetadataSectionProps) {
	const [open, setOpen] = useState(defaultOpen)
	const headingId = `section-${title.toLowerCase().replace(/\s+/g, '-')}`
	const contentVisible = !collapsible || open

	return (
		<section
			className={cn('space-y-2', className)}
			aria-labelledby={headingId}
		>
			{collapsible ? (
				<button
					type="button"
					id={headingId}
					onClick={() => setOpen((value) => !value)}
					className="flex w-full items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
					aria-expanded={open}
				>
					<div
						className={cn(
							'flex h-4 w-4 items-center justify-center rounded-full bg-primary/20 dark:bg-primary/30',
							iconWrapperClassName
						)}
					>
						<Icon className="h-2.5 w-2.5 text-primary" aria-hidden="true" />
					</div>
					<span className="min-w-0 flex-1 text-left">{title}</span>
					<ChevronDown
						className={cn(
							'h-4 w-4 shrink-0 transition-transform duration-200',
							open && 'rotate-180'
						)}
					/>
				</button>
			) : (
				<h3
					id={headingId}
					className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
				>
					<div
						className={cn(
							'flex h-4 w-4 items-center justify-center rounded-full bg-primary/20 dark:bg-primary/30',
							iconWrapperClassName
						)}
					>
						<Icon className="h-2.5 w-2.5 text-primary" aria-hidden="true" />
					</div>
					<span>{title}</span>
				</h3>
			)}
			{contentVisible && (
				<div className={cn('space-y-2 rounded-lg bg-muted/50 p-3', contentClassName)}>
					{children}
				</div>
			)}
		</section>
	)
}
